/**
 * Format selection — Protocol 1.02 ¶13 lists four acceptable
 * narrative formats: SBAR, SOAP, DCHARTE, DISPATCH. Selection is
 * deterministic from `call_data` per §3 of the design doc.
 */
import type { CallData } from './schema';

export type NarrativeFormat = 'DCHARTE' | 'SBAR' | 'DISPATCH' | 'SOAP';

const HIGH_ACUITY_TRIGGERS = [
  /cardiac\s+arrest/i,
  /stemi/i,
  /multi[-\s]system\s+trauma|major\s+trauma/i,
  /anaphylax/i,
  /sepsis/i,
  /\bob\b.*delivery|delivery|labor/i,
];

export function selectFormat(call: CallData): NarrativeFormat {
  const interventionCount = call.interventions.length + call.procedures.length;
  const text = [
    call.chief_complaint, call.field_diagnosis,
    call.protocols_invoked.join(' '),
  ].filter(Boolean).join(' ');

  // DCHARTE — high-acuity or 3+ interventions
  if (HIGH_ACUITY_TRIGGERS.some((re) => re.test(text)) || interventionCount >= 3) {
    return 'DCHARTE';
  }

  // SBAR — interfacility / alert activation / hand-off-driven
  if (call.is_interfacility) return 'SBAR';
  if (call.alert_activation && /stroke/i.test(call.alert_activation)) return 'SBAR';
  if (/bh\s+link|behavioral\s+health\s+link/i.test(call.destination ?? '')) return 'SBAR';

  // DISPATCH — scene dynamics or status change
  if (call.scene_complexity || call.status_change) return 'DISPATCH';

  // SOAP — default for standard medical complaints
  return 'SOAP';
}
